import React, { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu } from "lucide-react"
import { cn } from "@/lib/utils"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { TooltipProvider } from "@/components/ui/tooltip"
import { SidebarContent } from "./AppSidebar"

interface MobileHeaderProps {
  onOrgClick?: () => void
  onImpersonateClick?: () => void
}

/**
 * Phone-only shell chrome (hidden at `md` and up): a slim top bar with a
 * hamburger, the Pear logo and the current org name, plus a left-side drawer
 * that hosts the same {@link SidebarContent} as the desktop sidebar.
 *
 * The drawer closes itself on route change and whenever an action that opens
 * another full-screen surface (org switcher, impersonation picker) fires.
 */
export function MobileHeader({ onOrgClick, onImpersonateClick }: MobileHeaderProps) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { organizations, selectedOrganizationId } = useOrganizationsStore()
  const selectedOrg = organizations.find((org) => org.id === selectedOrganizationId)

  // Close on navigation. Depending on the full location key (not just the
  // pathname) also covers same-path navigations with a new search string.
  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])

  const closeThen = (fn?: () => void) => () => {
    setOpen(false)
    fn?.()
  }

  return (
    <>
      <header
        className={cn(
          "flex h-14 shrink-0 items-center gap-2 bg-pear-gradient px-2 text-white md:hidden"
        )}
      >
        <button
          type="button"
          aria-label="Open navigation"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/[0.08] hover:text-white"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
        </button>

        <Link to="/" className="flex shrink-0 items-center rounded-md" aria-label="Pear home">
          <img
            src={require("@/assets/pear_logo_white.svg").default}
            alt="Pear"
            className="h-6"
          />
        </Link>

        {selectedOrg && (
          <span className="ml-auto min-w-0 truncate pr-2 text-right text-[13px] font-medium text-white/80">
            {selectedOrg.name}
          </span>
        )}
      </header>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="w-[85vw] max-w-sm border-r-0 bg-pear-gradient p-0 text-white [&>button]:text-white [&>button]:focus:ring-white/40 [&>button]:focus:ring-offset-0"
        >
          {/* Radix requires a title for a11y; the drawer is visually self-describing. */}
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
          <TooltipProvider>
            <SidebarContent
              variant="drawer"
              onNavigate={() => setOpen(false)}
              onOrgClick={closeThen(onOrgClick)}
              onImpersonateClick={closeThen(onImpersonateClick)}
            />
          </TooltipProvider>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default MobileHeader
